import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Warning, CloudArrowUp, Envelope, EnvelopeOpen, ShieldWarning, Lightning, Receipt, Truck } from "@phosphor-icons/react";
import SettingsBackup from "@/pages/settings/SettingsBackup";
import SettingsEmail from "@/pages/settings/SettingsEmail";
import SettingsTemplates from "@/pages/settings/SettingsTemplates";
import SettingsDDTTemplates from "@/pages/settings/SettingsDDTTemplates";
import SettingsIntegrations from "@/pages/settings/SettingsIntegrations";

const WIPE_TOKEN = "WIPE_ALL_DATA_I_UNDERSTAND";

const TABS = [
  { key: "backup",    label: "Backup",      icon: CloudArrowUp,   testid: "tab-backup" },
  { key: "email",     label: "Email SMTP",  icon: Envelope,       testid: "tab-email" },
  { key: "templates", label: "Template Mail",    icon: EnvelopeOpen,   testid: "tab-templates" },
  { key: "ddt-templates", label: "Template DDT", icon: Receipt,  testid: "tab-ddt-templates" },
  { key: "transfer-templates", label: "Template Trasferimento", icon: Truck, testid: "tab-transfer-templates" },
  { key: "integrations", label: "Integrazioni", icon: Lightning,  testid: "tab-integrations" },
  { key: "danger",    label: "Zona Pericolosa", icon: ShieldWarning, testid: "tab-danger", danger: true },
];

export default function Settings() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("backup");
  const [showWipe, setShowWipe] = useState(false);
  const [wipeText, setWipeText] = useState("");
  const [wiping, setWiping] = useState(false);

  const doWipe = async () => {
    if (wipeText !== WIPE_TOKEN) return toast.error("Frase di conferma non corrispondente");
    setWiping(true);
    try {
      await api.post(`/admin/wipe-data?confirm=${encodeURIComponent(WIPE_TOKEN)}`);
      toast.success("Dati azzerati. Ricarico l'app...");
      setShowWipe(false); setWipeText("");
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (e) { toast.error(formatApiError(e)); } finally { setWiping(false); }
  };

  if (currentUser?.role !== "admin") return <div className="p-8 text-center text-neutral-500">Accesso riservato agli amministratori.</div>;

  return (
    <div>
      <PageHeader title="Impostazioni" subtitle="Manutenzione e configurazione avanzata dell'applicazione" />
      <div className="p-4 md:p-8 max-w-5xl">
        {/* Tab bar */}
        <div
          data-testid="settings-tabs"
          className="flex flex-wrap gap-0 mb-6 border-b border-neutral-200"
          role="tablist"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            const danger = t.danger;
            const base = "inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-[1px] transition-colors uppercase tracking-wider font-medium";
            const activeClass = danger
              ? "border-red-600 text-red-700"
              : "border-[#002FA7] text-[#002FA7]";
            const idleClass = danger
              ? "border-transparent text-red-600 hover:text-red-700 hover:bg-red-50"
              : "border-transparent text-neutral-600 hover:text-[#002FA7] hover:bg-neutral-50";
            return (
              <button
                key={t.key}
                data-testid={t.testid}
                onClick={() => setTab(t.key)}
                role="tab"
                aria-selected={active}
                className={`${base} ${active ? activeClass : idleClass}`}
              >
                <Icon size={16} weight={active ? "bold" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab body */}
        <div className="space-y-6" data-testid="settings-body">
          {tab === "backup" && <SettingsBackup />}
          {tab === "email" && <SettingsEmail />}
          {tab === "templates" && <SettingsTemplates />}
          {tab === "ddt-templates" && <SettingsDDTTemplates kind="ddt" />}
          {tab === "transfer-templates" && <SettingsDDTTemplates kind="transfer" />}
          {tab === "integrations" && <SettingsIntegrations />}
          {tab === "danger" && (
            <section className="border-2 border-red-300 bg-red-50 p-4 md:p-6" data-testid="danger-zone">
              <div className="flex items-start gap-3">
                <Warning size={22} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="label-eyebrow text-red-700 mb-1">Zona Pericolosa</div>
                  <h3 className="font-display font-bold text-lg tracking-tight text-red-900">Azzera tutti i dati</h3>
                  <p className="text-sm text-red-800 mt-1 max-w-2xl">
                    Elimina irreversibilmente prodotti, magazzini, seriali, movimenti, categorie, fornitori, clienti, commesse, scorte, notifiche push, log di audit e utenti non-admin. Solo il tuo account admin viene mantenuto e verrà ricreato un &laquo;Magazzino Centrale&raquo; di default.
                    <strong className="block mt-1">Operazione NON reversibile.</strong>
                  </p>
                  <button data-testid="open-wipe-btn" onClick={() => { setShowWipe(true); setWipeText(""); }}
                    className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm uppercase tracking-wider font-semibold">
                    Azzera tutti i dati
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {showWipe && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-red-400 shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto" data-testid="wipe-modal">
            <div className="p-5 border-b border-red-200 bg-red-50">
              <div className="flex items-center gap-2">
                <Warning size={22} weight="fill" className="text-red-600" />
                <h2 className="font-display font-bold text-lg tracking-tight text-red-900">Conferma azzeramento</h2>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-neutral-700">Stai per <strong>eliminare tutti i dati operativi</strong>. Solo il tuo account admin verrà mantenuto.</p>
              <div className="bg-neutral-100 p-3 text-xs font-mono select-all border border-neutral-200">{WIPE_TOKEN}</div>
              <label className="block">
                <span className="label-eyebrow block mb-2">Digita la frase qui sopra per confermare</span>
                <input autoFocus data-testid="wipe-input" value={wipeText} onChange={(e) => setWipeText(e.target.value)}
                  placeholder={WIPE_TOKEN}
                  className="w-full px-3 py-2 border border-neutral-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500" />
              </label>
            </div>
            <div className="p-5 pt-0 flex justify-end gap-2 border-t border-neutral-200 mt-4">
              <button data-testid="wipe-cancel" onClick={() => { setShowWipe(false); setWipeText(""); }} disabled={wiping} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50">Annulla</button>
              <button data-testid="wipe-confirm" onClick={doWipe} disabled={wipeText !== WIPE_TOKEN || wiping}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wider disabled:opacity-40">
                {wiping ? "Azzeramento..." : "Elimina tutto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
